<template>
	<div class="min-h-screen bg-gray-50">
		<div class="bg-white border-b border-gray-200 py-4 px-6">
			<h1 class="text-2xl font-semibold text-gray-900 m-0">Layouts</h1>
		</div>

		<div class="p-6">
			<div class="bg-white rounded-lg shadow-sm overflow-hidden">
				<div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
					<h2 class="text-lg font-semibold text-gray-900 m-0">All Layouts</h2>
					<button
						class="bg-orange-7 text-white border-none rounded-md py-2 px-4 text-sm font-medium cursor-pointer hover:bg-orange-6 transition-colors"
						@click="showCreateModal = true"
					>
						+ Add Layout
					</button>
				</div>

				<div class="flex justify-between items-center py-4 px-6 border-b border-gray-200 gap-4 flex-wrap">
					<div class="flex-1 min-w-[200px]">
						<input
							v-model="searchQuery"
							type="text"
							placeholder="Search layouts..."
							class="w-full max-w-[300px] py-2 px-3 border border-gray-300 rounded-md text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
						>
					</div>
					<div class="flex items-center gap-2">
						<label class="text-sm text-gray-500">Show:</label>
						<select v-model="pageSize" class="py-2 px-3 border border-gray-300 rounded-md text-sm cursor-pointer">
							<option :value="10">10</option>
							<option :value="25">25</option>
							<option :value="50">50</option>
							<option :value="100">100</option>
						</select>
					</div>
				</div>

				<div v-if="isLoading" class="flex justify-center items-center py-16 text-gray-500">
					Loading...
				</div>

				<div v-else class="overflow-x-auto">
					<table class="w-full border-collapse">
						<thead>
							<tr>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">ID</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">Name</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">Group ID</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">Type ID</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">Icon</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 w-[120px]">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="item in paginatedItems"
								:key="item.id"
								class="border-b border-gray-200 hover:bg-gray-50 transition-colors"
							>
								<td class="py-4 px-6 text-sm text-gray-900">{{ item.id }}</td>
								<td class="py-4 px-6 text-sm text-gray-900">{{ item.name }}</td>
								<td class="py-4 px-6 text-sm text-gray-500">{{ item.groupid ?? '-' }}</td>
								<td class="py-4 px-6 text-sm text-gray-500">{{ item.typeid ?? '-' }}</td>
								<td class="py-4 px-6 text-sm">
									<img
										v-if="item.icon"
										:src="item.icon"
										class="w-8 h-8 object-contain rounded"
										alt="Icon"
									>
									<span v-else class="text-gray-400 italic text-xs">No icon</span>
								</td>
								<td class="py-4 px-6 text-sm">
									<div class="flex items-center gap-2">
										<button
											class="p-2 bg-orange-2 text-orange-7 border-none rounded-md cursor-pointer hover:bg-orange-3 transition-colors"
											title="Edit"
											@click="editItem(item.id)"
										>
											<i class="fa fa-edit"></i>
										</button>
										<button
											class="p-2 bg-red-100 text-red-600 border-none rounded-md cursor-pointer hover:bg-red-200 transition-colors"
											title="Delete"
											@click="confirmDelete(item)"
										>
											<i class="fa fa-trash"></i>
										</button>
									</div>
								</td>
							</tr>
							<tr v-if="paginatedItems.length === 0">
								<td class="py-4 px-6 text-sm text-gray-500" colspan="6">
									<div class="text-center py-8 text-gray-400">No layouts found</div>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<div class="flex justify-between items-center py-4 px-6 border-t border-gray-200 bg-gray-50 flex-wrap gap-4">
					<div class="text-sm text-gray-500">
						Showing {{ startRow }} to {{ endRow }} of {{ totalRows }} entries
					</div>
					<div class="flex items-center gap-2">
						<button
							class="py-2 px-3 bg-white border border-gray-300 rounded-md text-sm cursor-pointer hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
							:disabled="currentPage === 1"
							@click="goToPage(1)"
						>
							First
						</button>
						<button
							class="py-2 px-3 bg-white border border-gray-300 rounded-md text-sm cursor-pointer hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
							:disabled="currentPage === 1"
							@click="goToPage(currentPage - 1)"
						>
							Previous
						</button>
						<span class="py-2 px-4 text-sm text-gray-600">
							Page {{ currentPage }} of {{ totalPages }}
						</span>
						<button
							class="py-2 px-3 bg-white border border-gray-300 rounded-md text-sm cursor-pointer hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
							:disabled="currentPage === totalPages"
							@click="goToPage(currentPage + 1)"
						>
							Next
						</button>
						<button
							class="py-2 px-3 bg-white border border-gray-300 rounded-md text-sm cursor-pointer hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
							:disabled="currentPage === totalPages"
							@click="goToPage(totalPages)"
						>
							Last
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Create Modal -->
		<div v-if="showCreateModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50" @click.self="showCreateModal = false">
			<div class="bg-white rounded-lg shadow-2xl w-full max-w-md overflow-hidden">
				<div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
					<h3 class="text-lg font-semibold text-gray-900 m-0">Create New Layout</h3>
					<button class="text-2xl text-gray-400 bg-transparent border-none cursor-pointer hover:text-gray-600" @click="showCreateModal = false">&times;</button>
				</div>
				<div class="p-6">
					<div class="mb-4">
						<label class="block text-sm font-medium text-gray-700 mb-1.5">Name *</label>
						<input
							v-model="newItemName"
							type="text"
							placeholder="Enter layout name"
							class="w-full py-2.5 px-3 border border-gray-300 rounded-md text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
						>
					</div>
				</div>
				<div class="flex justify-end gap-3 py-4 px-6 border-t border-gray-200 bg-gray-50">
					<button
						class="py-2 px-4 bg-white text-gray-700 border border-gray-300 rounded-md text-sm font-medium cursor-pointer hover:bg-gray-50 transition-colors"
						@click="showCreateModal = false"
					>
						Cancel
					</button>
					<button
						class="py-2 px-4 bg-orange-7 text-white border-none rounded-md text-sm font-medium cursor-pointer hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
						:disabled="!newItemName.trim() || isCreating"
						@click="createItem"
					>
						{{ isCreating ? 'Creating...' : 'Create' }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { LayoutModel } from '@sosocio/creator/interfaces';
import Swal from 'sweetalert2';
import LayoutService from 'services/LayoutService';

export default defineComponent({
	name: 'LayoutsPage',
	setup() {
		const router = useRouter();

		const items = ref<LayoutModel[]>([]);
		const searchQuery = ref('');
		const currentPage = ref(1);
		const pageSize = ref(25);
		const isLoading = ref(true);
		const showCreateModal = ref(false);
		const newItemName = ref('');
		const isCreating = ref(false);

		const filteredItems = computed(() => {
			if (!searchQuery.value.trim()) {
				return items.value;
			}
			const query = searchQuery.value.toLowerCase();
			return items.value.filter(item =>
				item.name?.toLowerCase().includes(query) ||
				String(item.id).includes(query) ||
				String(item.groupid).includes(query) ||
				String(item.typeid).includes(query)
			);
		});

		const totalRows = computed(() => filteredItems.value.length);
		const totalPages = computed(() => Math.max(1, Math.ceil(totalRows.value / pageSize.value)));
		const startRow = computed(() => totalRows.value === 0 ? 0 : (currentPage.value - 1) * pageSize.value + 1);
		const endRow = computed(() => Math.min(currentPage.value * pageSize.value, totalRows.value));

		const paginatedItems = computed(() => {
			const start = (currentPage.value - 1) * pageSize.value;
			return filteredItems.value.slice(start, start + pageSize.value);
		});

		watch([searchQuery, pageSize], () => {
			currentPage.value = 1;
		});

		const goToPage = (page: number) => {
			if (page >= 1 && page <= totalPages.value) {
				currentPage.value = page;
			}
		};

		const loadData = async () => {
			isLoading.value = true;
			try {
				const params = new URLSearchParams({
					limit: '0',
				});
				const data = await LayoutService.getAll(params);
				items.value = data;
			} catch (error: any) {
				console.error('Failed to load layouts:', error);
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to load layouts',
					icon: 'error',
					customClass: {
						popup: 'windmill-swal-popup',
						title: 'windmill-swal-title',
						htmlContainer: 'windmill-swal-content',
						confirmButton: 'windmill-swal-confirm',
					},
				});
			} finally {
				isLoading.value = false;
			}
		};

		const editItem = (id: number) => {
			router.push(`/layout/${id}`);
		};

		const confirmDelete = async (item: LayoutModel) => {
			const result = await Swal.fire({
				title: 'Delete Layout?',
				text: `Are you sure you want to delete "${item.name || 'this item'}"? This action cannot be undone.`,
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#dc2626',
				cancelButtonColor: '#6b7280',
				confirmButtonText: 'Yes, delete it',
				cancelButtonText: 'Cancel',
				customClass: {
					popup: 'windmill-swal-popup',
					title: 'windmill-swal-title',
					htmlContainer: 'windmill-swal-content',
					confirmButton: 'windmill-swal-confirm-danger',
					cancelButton: 'windmill-swal-cancel',
				},
			});

			if (result.isConfirmed) {
				try {
					await LayoutService.delete(item.id);
					items.value = items.value.filter(i => i.id !== item.id);
					Swal.fire({
						toast: true,
						position: 'top-end',
						icon: 'success',
						title: 'Layout deleted successfully',
						showConfirmButton: false,
						timer: 3000,
					});
				} catch (error: any) {
					Swal.fire({
						title: 'Error',
						text: error.message || 'Failed to delete layout',
						icon: 'error',
						customClass: {
							popup: 'windmill-swal-popup',
							title: 'windmill-swal-title',
							htmlContainer: 'windmill-swal-content',
							confirmButton: 'windmill-swal-confirm',
						},
					});
				}
			}
		};

		const createItem = async () => {
			if (!newItemName.value.trim()) return;

			isCreating.value = true;
			try {
				const newLayout = await LayoutService.create({
					name: newItemName.value.trim(),
				});
				showCreateModal.value = false;
				newItemName.value = '';
				router.push(`/layout/${newLayout.id}`);
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to create layout',
					icon: 'error',
					customClass: {
						popup: 'windmill-swal-popup',
						title: 'windmill-swal-title',
						htmlContainer: 'windmill-swal-content',
						confirmButton: 'windmill-swal-confirm',
					},
				});
			} finally {
				isCreating.value = false;
			}
		};

		onMounted(() => {
			loadData();
		});

		return {
			items,
			searchQuery,
			currentPage,
			pageSize,
			isLoading,
			showCreateModal,
			newItemName,
			isCreating,
			filteredItems,
			totalRows,
			totalPages,
			startRow,
			endRow,
			paginatedItems,
			goToPage,
			editItem,
			confirmDelete,
			createItem,
		};
	},
});
</script>
